import React, { useEffect, memo, FocusEvent } from 'react'
import { useStyles, useTooltipStyles } from './styles'

import {
  TextField as MuiTextField,
  Tooltip,
  TextFieldProps as MuiTextFieldProps,
  CircularProgress,
  SxProps,
  InputLabelProps,
  InputAdornment,
  IconButton,
} from '@mui/material'

import { useDebounce } from '../../Hooks/useDebounce'
import { useDerivedState } from '../../Hooks/UseDerivedState'
import { J2Multiply } from '@e3dc-react/icons'
import { PatternFormat } from 'react-number-format'

export interface TextFieldBaseProps {
  onChange?: (value: string, fieldName: string) => void
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  label?: string | React.ReactElement
  /** delay in miliseconds after which the callback will be fired. Prevents firing callback after each value change. Default value = 300 */
  delayInMs?: number
  disabled?: boolean
  required?: boolean
  clearable?: boolean
  name?: string
  value?: string
  textAlign?: 'left' | 'right' | 'center'
  withTrim?: boolean
  mask?: string
}

export type InputType = 'password' | 'email' | 'time' | 'date' | 'datetime' | 'number'

export interface TextFieldProps extends TextFieldBaseProps {
  variant?: MuiTextFieldProps['variant']
  placeholder?: string
  min?: number
  max?: number
  minLength?: number
  maxLength?: number
  className?: string
  resetValue?: number
  helperText?: string
  fullWidth?: boolean
  multiline?: boolean
  spaceRight?: boolean
  rows?: number
  rowsMax?: number
  endAdornment?: React.ReactElement
  startAdornment?: React.ReactElement
  suffix?: string
  prefix?: string | React.ReactNode
  type?: InputType
  disableAutoComplete?: boolean
  autoFocus?: boolean
  autoComplete?: string
  loading?: boolean
  highlighted?: boolean
  sx?: SxProps
  InputLabelProps?: Partial<InputLabelProps>
  useTooltipErrorMessage?: boolean
  errorZIndex?: number
  step?: number
  useDebounceDelay?: boolean
  error?: boolean
  ref?: React.Ref<HTMLDivElement>
  dataQa?: string
}

export const TextField = React.forwardRef<unknown, TextFieldProps>(
  (
    {
      onChange,
      onBlur: onBlurProps,
      placeholder,
      value,
      label,
      helperText,
      className,
      rows,
      rowsMax,
      name,
      endAdornment,
      startAdornment,
      prefix,
      suffix,
      type,
      max,
      maxLength,
      minLength,
      variant = 'outlined',
      delayInMs = 300,
      disabled = false,
      required = false,
      clearable = true,
      multiline = false,
      fullWidth = true,
      spaceRight = false,
      disableAutoComplete = false,
      autoFocus = false,
      loading = false,
      highlighted = false,
      useTooltipErrorMessage = true,
      useDebounceDelay = true,
      step = 1,
      errorZIndex,
      error,
      withTrim,
      mask,
      dataQa,
      ...otherProps
    },
    ref
  ) => {
    const [textFieldValue, setTextFieldValue] = useDerivedState(value ?? '')
    const debouncedValue = useDebounce(textFieldValue, delayInMs)
    const { classes, cx } = useTooltipStyles({ zIndex: errorZIndex })
    const { classes: inputStyles } = useStyles({ hasSuffix: !!suffix, highlighted })

    useEffect(() => {
      setTextFieldValue(value ?? '')
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    useEffect(() => {
      if (onChange && !useDebounceDelay) onChange(debouncedValue, name as string)
      if (onChange && value !== debouncedValue && useDebounceDelay) onChange(debouncedValue, name as string)
    }, [debouncedValue]) // eslint-disable-line

    const onBlur = (e: FocusEvent<HTMLInputElement>): void => {
      onBlurProps?.(e)

      if (withTrim) {
        setTextFieldValue(e.target.value?.trim())
      }
    }

    const inputProps = {
      onBlur,
      endAdornment: (
        <InputAdornment position="end">
          {suffix && <div>{suffix}</div>}
          {clearable && !disabled && value !== '' && value !== undefined && (
            <IconButton onClick={() => setTextFieldValue('')} classes={{ root: inputStyles.searchFieldIcon }} size="large">
              <J2Multiply />
            </IconButton>
          )}
          {endAdornment}
          {loading && <CircularProgress size={24} />}
        </InputAdornment>
      ),
      startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : startAdornment ?? null,
      classes: {
        disabled: inputStyles.disabled,
      },
      autoComplete: disableAutoComplete && type === 'password' ? 'new-password' : disableAutoComplete ? 'off' : 'on',
      inputProps: {
        maxLength: maxLength,
        minLength: minLength,
        step,
        'data-qa': dataQa,
      },
    }

    const TextField = (
      <MuiTextField
        inputRef={ref}
        onChange={event => setTextFieldValue(event.target.value)}
        type={type ?? 'text'}
        label={label}
        fullWidth={fullWidth}
        error={Boolean(helperText) || error}
        helperText={useTooltipErrorMessage ? '' : helperText}
        value={textFieldValue || ''}
        className={cx(className, spaceRight ? inputStyles.spaceRight : undefined)}
        variant={variant}
        placeholder={placeholder}
        margin="dense"
        name={name}
        required={required}
        disabled={disabled}
        multiline={multiline}
        rows={multiline ? rows ?? 5 : undefined}
        maxRows={multiline ? rowsMax ?? 10 : undefined}
        autoFocus={autoFocus}
        InputProps={inputProps}
        InputLabelProps={{
          classes: { root: inputStyles.inputLabel },
        }}
        classes={{ root: inputStyles.textField }}
        // data-qa={dataQa}
        {...otherProps}
      />
    )
    if (mask) {
      return (
        <PatternFormat
          format={mask}
          customInput={MuiTextField}
          value={textFieldValue}
          onValueChange={({ value }) => {
            setTextFieldValue(value)
          }}
          onBlur={event => {
            onBlur(event)
          }}
          inputRef={ref}
          label={label}
          fullWidth={fullWidth}
          error={Boolean(helperText) || error}
          helperText={useTooltipErrorMessage ? '' : helperText}
          className={cx(className, spaceRight ? inputStyles.spaceRight : undefined)}
          variant={variant}
          placeholder={placeholder}
          margin="dense"
          disabled={disabled}
          required={required}
          multiline={multiline}
          autoFocus={autoFocus}
          InputProps={inputProps}
          name={name}
          rows={multiline ? rows ?? 5 : undefined}
          maxRows={multiline ? rowsMax ?? 10 : undefined}
          InputLabelProps={{
            classes: { root: inputStyles.inputLabel },
          }}
          classes={{ root: inputStyles.textField }}
          data-qa={dataQa}
          {...otherProps}
        />
      )
    }
    if (useTooltipErrorMessage) {
      return (
        <Tooltip
          title={helperText || ''}
          open={!!helperText}
          arrow
          classes={classes}
          PopperProps={{
            disablePortal: true,
            modifiers: [
              {
                name: 'flip',
                options: {
                  fallbackPlacements: [],
                },
              },
            ],
          }}
        >
          {TextField}
        </Tooltip>
      )
    }

    return TextField
  }
)

TextField.displayName = 'TextField'

export const MemoizedTextField = memo(TextField)
